import {
  Box,
  Typography,
  TextField,
} from "@mui/material";

import renderInputMatchAdornment from "./InputMatchAdornment";
import TagsField from "../../components/TagsField";
import CategorySelector from "../../components/CategorySelector";
import SizeSelector from "../../components/SizeSelector";
import LocationsSelector from "../../components/LocationsSelector";
import ContainerOptionSelector from "../../components/ContainerOptionSelector";
import PresentationSelector from "../../components/PresentationSelector";
import TypeSelector from "../../components/TypeSelector";
import PrimaryClassificationSelector from "../../components/PrimaryClassificationSelector";
import SecondaryClassificationSelector from "../../components/SecondaryClassificationSelector";
import ProducerSelector from "../../components/ProducerSelector";

export default function ClassificationSection({
  isEditing,
  values,
  setValues,
  cin7Product,
  shopifyProduct,
  error,
}) {

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Classification and Origin
      </Typography>

      {/* CATEGORY - TYPE - SUBTYPE - SUBSUBTYPE */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2,
          mb: 2,
        }}
      >
        {/* Category */}
        <CategorySelector
          value={values.category}
          onChange={(newValue) =>
            setValues((prev) => ({
              ...prev,
              category: newValue,
            }))
          }
          required
          error={error && !values.category}
          helperText="Required field"
          isEditing={isEditing}
          cin7Category={cin7Product?.category}
          shopifyCategory={shopifyProduct?.productType}
        />

        <Box sx={{ gridColumn: "2 / span 3" }}>
          <TypeSelector values={values} setValues={setValues} />
        </Box>
      </Box>

      {/* SIZE - PRESENTATION - CONTAINER - WEIGHT */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2,
          mb: 2,
        }}
      >
        {/* Size */}
        <SizeSelector
          value={values.metafields?.size || ""}
          onChange={(value) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), size: value },
            }))
          }
        />

        {/* Presentation */}
        <PresentationSelector
          value={values.metafields?.presentation}
          onChange={(value) =>
            setValues((prev) => ({
              ...prev,
              metafields: {
                ...(prev.metafields || {}),
                presentation: value,
              },
            }))
          }
        />

        {/* Container */}
        <ContainerOptionSelector
          value={values.metafields?.container}
          onChange={(newValue) =>
            setValues((prev) => ({
              ...prev,
              metafields: {
                ...(prev.metafields || {}),
                container: newValue,
              },
            }))
          }
        />

        {/* Weight */}
        <TextField
          label="Weight (kg)"
          type="number"
          value={values.weight}
          onChange={(e) =>
            setValues((prev) => ({ ...prev, weight: e.target.value }))
          }
          fullWidth
          required
          size="small"
          error={error && !values.weight}
          helperText="Required field"
          slotProps={{
            input: renderInputMatchAdornment(
              isEditing,
              cin7Product?.weight,
              shopifyProduct?.variant?.weight
            ),
          }}
        />
      </Box>

      {/* COUNTRY - REGION - SUBREGION - ABV */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ gridColumn: "1 / span 3" }}>
          <LocationsSelector values={values} setValues={setValues} />
        </Box>

        {/* ABV */}
        <TextField
          label="ABV"
          type="number"
          value={values.metafields?.abv || ""}
          onChange={(e) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), abv: e.target.value },
            }))
          }
          fullWidth
          size="small"
        />
      </Box>

      {/* PRODUCER - PRIMARY & SECONDARY CLASSIFICATION */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          mt: 3,
          mb: 3,
        }}
      >
        <ProducerSelector
          value={values.metafields?.producer}
          onChange={(value) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), producer: value },
            }))
          }
        />
        <PrimaryClassificationSelector
          value={values.metafields?.primary_classification}
          onChange={(value) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), primary_classification: value },
            }))
          }
        />
        <SecondaryClassificationSelector
          value={values.metafields?.secondary_classification}
          onChange={(value) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), secondary_classification: value },
            }))
          }
        />
      </Box>

      {/* TAGS */}
      <Box sx={{ gridColumn: "1 / span 2" }}>
        <TagsField
          value={values.tags}
          disablePadding={true}
          onChange={(value) => setValues((prev) => ({ ...prev, tags: value }))}
          isEditingMode={true}
        />
      </Box>
    </Box>
  );
}
